import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { motion } from "framer-motion";
import { h1, btnPrimary } from "./ui";
import { Sparkles, Send, Loader2, User, CheckCircle2, X, ClipboardCheck, Mic, Square, PanelLeft } from "lucide-react";
import ConversationList from "./assistant/ConversationList";

const SUGGESTIONS = [
  "Cadastre a paciente Ana Souza",
  "Agende uma sessão para Ana amanhã às 15h por R$200",
  "Prepare um WhatsApp para a Ana confirmando a sessão",
  "Resuma o histórico da paciente Ana",
];

export default function Assistente() {
  const [conversations, setConversations] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const endRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const loadConversations = useCallback(() => api.get("/conversations").then(({ data }) => setConversations(data)).catch(() => {}), []);
  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const selectConversation = async (id) => {
    setListOpen(false);
    if (id === currentId) return;
    setCurrentId(id);
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      setMessages(data.map((m) => ({ role: m.role, content: m.content })));
    } catch { setMessages([]); }
  };

  const newConversation = () => { setCurrentId(null); setMessages([]); setListOpen(false); };

  const onConversationChanged = (deletedId) => {
    loadConversations();
    if (deletedId && deletedId === currentId) newConversation();
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: msg, conversation_id: currentId });
      if (data.conversation_id && data.conversation_id !== currentId) { setCurrentId(data.conversation_id); loadConversations(); }
      setMessages((m) => {
        const next = [...m];
        if (data.reply) next.push({ role: "assistant", content: data.reply });
        if (data.pending_actions?.length) next.push({ role: "actions", actions: data.pending_actions, status: "pending", id: `act-${Date.now()}` });
        return next;
      });
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: formatApiError(e.response?.data?.detail) || "Não consegui responder agora." }]);
    } finally { setLoading(false); }
  };

  const confirm = async (msg) => {
    if (loading) return;
    setLoading(true);
    const results = [];
    let allOk = true;
    for (const a of msg.actions) {
      try {
        const { data } = await api.post("/assistant/execute", { tool: a.tool, args: a.args, conversation_id: currentId });
        if (!data.ok) allOk = false;
        if (data.whatsapp_url) window.open(data.whatsapp_url, "_blank");
        results.push(data.ok ? `✓ ${data.message}` : `⚠️ ${data.message}`);
      } catch { allOk = false; results.push(`⚠️ Não consegui executar: ${a.summary}`); }
    }
    setMessages((m) => m.map((x) => (x.id === msg.id ? { ...x, status: "confirmed" } : x)).concat([{ role: "assistant", content: results.join("\n") }]));
    if (allOk) toast.success("Ação executada."); else toast.warning("Concluído com avisos. Veja os detalhes no chat.");
    setLoading(false);
  };

  const cancel = (msg) => {
    setMessages((m) => m.map((x) => (x.id === msg.id ? { ...x, status: "cancelled" } : x)).concat([{ role: "assistant", content: "Sem problema, não fiz nenhuma alteração." }]));
  };

  const transcribeAndSend = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "audio.webm");
      const { data } = await api.post("/assistant/transcribe", fd);
      if (data.text?.trim()) await send(data.text.trim());
      else toast.error("Não entendi o áudio. Tente novamente.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao transcrever o áudio.");
    } finally { setTranscribing(false); }
  };

  const toggleRecording = async () => {
    if (recording) { recorderRef.current?.stop(); return; }
    if (!navigator.mediaDevices?.getUserMedia) return toast.error("Seu navegador não suporta gravação de áudio.");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size > 0) transcribeAndSend(blob);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch { toast.error("Permita o acesso ao microfone para usar o áudio."); }
  };

  return (
    <div className="flex gap-5 h-[calc(100vh-8rem)] lg:h-[calc(100vh-6rem)]">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col rounded-2xl bg-white/60 border border-[#5B6E5B]/10 p-3">
        <ConversationList conversations={conversations} currentId={currentId} onSelect={selectConversation} onNew={newConversation} onChanged={onConversationChanged} />
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-body text-xs tracking-[0.35em] uppercase text-[#C87A5E]">Assistente IA</p>
            <h1 className={h1}>Sua secretária 🌿</h1>
            <p className="font-body text-[#4A5D4E] mt-2 text-sm">Ela lembra das conversas. Peça para consultar ou <strong>fazer</strong> coisas — mostro um resumo para você confirmar antes de gravar.</p>
          </div>
          <button onClick={() => setListOpen((v) => !v)} data-testid="toggle-conversations" className="lg:hidden shrink-0 grid place-items-center w-10 h-10 rounded-full border border-[#5B6E5B]/20 text-[#5B6E5B]"><PanelLeft className="w-5 h-5" /></button>
        </div>

        {listOpen && (
          <div className="lg:hidden mt-3 rounded-2xl bg-white border border-[#5B6E5B]/15 p-3 max-h-72 overflow-hidden" data-testid="mobile-conversations">
            <ConversationList conversations={conversations} currentId={currentId} onSelect={selectConversation} onNew={newConversation} onChanged={onConversationChanged} />
          </div>
        )}

        <div className="flex-1 overflow-y-auto mt-4 rounded-2xl bg-white border border-[#5B6E5B]/10 p-5 space-y-4" data-testid="chat-window">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center gap-5 py-10">
              <div className="grid place-items-center w-14 h-14 rounded-full bg-[#F5F0E6]"><Sparkles className="w-6 h-6 text-[#5B6E5B]" /></div>
              <p className="font-body text-sm text-[#7A8A7A] max-w-sm">Experimente uma dessas:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} data-testid="chat-suggestion" className="rounded-full border border-[#5B6E5B]/20 px-4 py-2 text-sm text-[#4A5D4E] hover:bg-[#F5F0E6] transition-colors">{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => {
            if (m.role === "actions") {
              return (
                <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex gap-3" data-testid="action-card">
                  <div className="shrink-0 grid place-items-center w-9 h-9 rounded-full bg-[#C87A5E]"><ClipboardCheck className="w-4 h-4 text-[#FDFBF7]" /></div>
                  <div className="max-w-[85%] rounded-2xl border border-[#C87A5E]/30 bg-[#C87A5E]/8 px-4 py-3 w-full">
                    <p className="font-body text-xs uppercase tracking-wider text-[#B2644D] mb-2">Confirme a ação</p>
                    <ul className="space-y-1.5">
                      {m.actions.map((a, j) => (<li key={j} className="font-body text-sm text-[#2C332A] flex gap-2" data-testid="action-summary"><span className="text-[#C87A5E]">•</span> {a.summary}</li>))}
                    </ul>
                    {m.status === "pending" ? (
                      <div className="flex gap-2 mt-4">
                        <button onClick={() => confirm(m)} disabled={loading} data-testid="confirm-action-btn" className="inline-flex items-center gap-2 rounded-full bg-[#5B6E5B] px-4 py-2 text-sm text-[#FDFBF7] hover:bg-[#4A5D4E] transition-colors disabled:opacity-60"><CheckCircle2 className="w-4 h-4" /> Confirmar</button>
                        <button onClick={() => cancel(m)} disabled={loading} data-testid="cancel-action-btn" className="inline-flex items-center gap-2 rounded-full border border-[#5B6E5B]/25 px-4 py-2 text-sm text-[#4A5D4E] hover:bg-white transition-colors"><X className="w-4 h-4" /> Cancelar</button>
                      </div>
                    ) : (<p className={`font-body text-xs mt-3 ${m.status === "confirmed" ? "text-[#5B6E5B]" : "text-[#7A8A7A]"}`}>{m.status === "confirmed" ? "✓ Confirmado" : "Cancelado"}</p>)}
                  </div>
                </motion.div>
              );
            }
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
                {m.role === "assistant" && <div className="shrink-0 grid place-items-center w-9 h-9 rounded-full bg-[#5B6E5B]"><Sparkles className="w-4 h-4 text-[#FDFBF7]" /></div>}
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 font-body text-sm whitespace-pre-line ${m.role === "user" ? "bg-[#5B6E5B] text-[#FDFBF7]" : "bg-[#F5F0E6] text-[#2C332A]"}`}>{m.content}</div>
                {m.role === "user" && <div className="shrink-0 grid place-items-center w-9 h-9 rounded-full bg-[#F5F0E6]"><User className="w-4 h-4 text-[#5B6E5B]" /></div>}
              </motion.div>
            );
          })}
          {loading && (
            <div className="flex gap-3" data-testid="chat-loading">
              <div className="shrink-0 grid place-items-center w-9 h-9 rounded-full bg-[#5B6E5B]"><Sparkles className="w-4 h-4 text-[#FDFBF7]" /></div>
              <div className="bg-[#F5F0E6] rounded-2xl px-4 py-3"><Loader2 className="w-4 h-4 animate-spin text-[#5B6E5B]" /></div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-3" data-testid="chat-form">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Peça algo à sua secretária..." data-testid="chat-input"
            className="flex-1 rounded-full bg-white border border-[#5B6E5B]/15 px-5 py-3 font-body text-sm text-[#2C332A] outline-none focus:border-[#C87A5E] focus:ring-2 focus:ring-[#C87A5E]/25" />
          <button type="button" onClick={toggleRecording} disabled={loading || transcribing} data-testid="chat-mic" title={recording ? "Parar gravação" : "Gravar áudio"}
            className={`grid place-items-center w-12 h-12 shrink-0 rounded-full transition-colors disabled:opacity-60 ${recording ? "bg-[#C87A5E] text-[#FDFBF7] animate-pulse" : "border border-[#5B6E5B]/25 text-[#5B6E5B] hover:bg-[#F5F0E6]"}`}>
            {transcribing ? <Loader2 className="w-5 h-5 animate-spin" /> : recording ? <Square className="w-4 h-4" /> : <Mic className="w-5 h-5" />}
          </button>
          <button type="submit" disabled={loading} className={btnPrimary} data-testid="chat-send">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
        {(recording || transcribing) && (
          <p className="font-body text-xs text-[#C87A5E] mt-2" data-testid="mic-status">{recording ? "Gravando... toque no quadrado para enviar." : "Transcrevendo seu áudio..."}</p>
        )}
      </div>
    </div>
  );
}
